import { Component, EventEmitter, Input, Output, forwardRef, inject } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { FileUploaderBaseComponent } from "../../../../Common/Components/FileUploader/FileUploaderComponent";
import { FileUploaderItem, FileUploaderViewMode } from "../../../../Common/Components/FileUploader/Dto/FileUploaderItem";
import { ConfirmModalEffects } from "../../../../Common/Components/ConfirmModal/State/ConfirmModalEffects";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";
import { FileApi } from "../../Api/FileApi";
import { FileUploadEntry } from "../../Dto/FileUploadEntry";
import { File as UploadedFile } from "../../Dto/FileModel";

@Component({
  selector: "app-file-uploader",
  standalone: true,
  imports: [FileUploaderBaseComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploaderComponent),
      multi: true,
    },
  ],
  templateUrl: "./FileUploaderComponent.html",
  styleUrl: "./FileUploaderComponent.scss",
})
export class FileUploaderComponent implements ControlValueAccessor {
  private readonly fileApi = inject(FileApi);
  private readonly confirmModalEffects = inject(ConfirmModalEffects);
  private onChange: (value: UploadedFile[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private itemCounter = 0;

  @Input({ required: true }) folder = "";
  @Input() multiple = false;
  @Input() accept = "*/*";
  @Input() disabled = false;
  @Input() viewMode: FileUploaderViewMode = "list";
  @Input() label = "Upload files";
  @Input() hint = "Drag and drop files here, or click to browse.";
  @Input() emptyStateTitle = "No files uploaded";
  @Input() emptyStateDescription = "Uploaded files will appear here after they finish uploading.";

  @Output() filesChange = new EventEmitter<UploadedFile[]>();
  @Output() uploadError = new EventEmitter<string>();

  items: FileUploadEntry[] = [];
  isUploading = false;
  errorMessage: string | null = null;

  get baseItems(): FileUploaderItem[] {
    return this.items.map((item) => ({
      id: item.id,
      name: item.name,
      size: item.size,
      status: item.status,
      publicUrl: item.file?.publicUrl,
      mimeType: item.file?.mimeType,
    }));
  }

  get uploadedFiles(): UploadedFile[] {
    return this.items.flatMap((item) => {
      if (!item.file) {
        return [];
      }

      return [item.file];
    });
  }

  writeValue(value: UploadedFile[] | null): void {
    const uploadedFiles = this.normalizeUploadedFiles(value);
    this.items = uploadedFiles.map((file) => this.createUploadedItem(file));
  }

  registerOnChange(fn: (value: UploadedFile[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  async onFilesSelected(files: globalThis.File[]): Promise<void> {
    const uploadFolder = this.resolveUploadFolder();
    if (!uploadFolder) {
      return;
    }

    if (this.isInteractionDisabled()) {
      return;
    }

    const selectedFiles = this.normalizeSelectedFiles(files);
    if (!selectedFiles.length) {
      return;
    }

    this.onTouched();
    this.errorMessage = null;
    this.isUploading = true;
    this.items = this.buildUploadingState(selectedFiles);

    try {
      const uploadedFiles = await this.uploadSelectedFiles(selectedFiles, uploadFolder);
      this.items = this.buildUploadedState(uploadedFiles);
      this.syncValue();
    } catch (exception: unknown) {
      this.items = this.uploadedItems();
      this.errorMessage = getBackendErrorMessage(exception, "Failed to upload file.");
      this.uploadError.emit(this.errorMessage);
    } finally {
      this.isUploading = false;
    }
  }

  removeFile(itemId: string): void {
    if (this.isInteractionDisabled()) {
      return;
    }

    const item = this.items.find((entry) => entry.id === itemId);
    const itemName = this.getRemovalItemName(item);

    this.confirmModalEffects.open({
      title: "Remove File",
      message: `Are you sure you want to remove "${itemName}" from this uploader?`,
      confirmText: "Remove File",
      cancelText: "Cancel",
      variant: "danger",
      closeOnOverlay: true,
      onConfirm: async () => {
        this.performRemoveFile(itemId);
      },
    });
  }

  private normalizeUploadedFiles(value: UploadedFile[] | null): UploadedFile[] {
    if (!Array.isArray(value)) {
      return [];
    }

    if (!this.multiple) {
      return value.slice(0, 1);
    }

    return value;
  }

  private normalizeSelectedFiles(files: globalThis.File[]): globalThis.File[] {
    if (!this.multiple) {
      return files.slice(0, 1);
    }

    return files;
  }

  private buildUploadingState(files: globalThis.File[]): FileUploadEntry[] {
    const uploadingItems = files.map((file) => this.createUploadingItem(file));
    if (!this.multiple) {
      return uploadingItems;
    }

    return [...this.uploadedItems(), ...uploadingItems];
  }

  private buildUploadedState(uploadedFiles: UploadedFile[]): FileUploadEntry[] {
    const uploadedItems = uploadedFiles.map((file) => this.createUploadedItem(file));
    if (!this.multiple) {
      return uploadedItems;
    }

    return [...this.uploadedItems(), ...uploadedItems];
  }

  private async uploadSelectedFiles(files: globalThis.File[], folder: string): Promise<UploadedFile[]> {
    if (files.length === 1) {
      return [await this.fileApi.uploadFile(files[0], { folder })];
    }

    return this.fileApi.uploadFiles(files, { folder });
  }

  private uploadedItems(): FileUploadEntry[] {
    return this.items.filter((item) => item.status === "uploaded");
  }

  private createUploadingItem(file: globalThis.File): FileUploadEntry {
    return {
      id: this.nextItemId(),
      name: file.name,
      size: file.size,
      status: "uploading",
    };
  }

  private createUploadedItem(file: UploadedFile): FileUploadEntry {
    return {
      id: file.id,
      name: file.originalName,
      size: file.size,
      status: "uploaded",
      file,
    };
  }

  private nextItemId(): string {
    this.itemCounter += 1;
    return `file-uploader-item-${this.itemCounter}`;
  }

  private syncValue(): void {
    const value = this.uploadedFiles;
    this.onChange(value);
    this.filesChange.emit(value);
  }

  private performRemoveFile(itemId: string): void {
    this.onTouched();
    this.items = this.items.filter((item) => item.id !== itemId);
    this.syncValue();
  }

  private getRemovalItemName(item: FileUploadEntry | undefined): string {
    if (item?.name?.trim()) {
      return item.name.trim();
    }

    return "this file";
  }

  private isInteractionDisabled(): boolean {
    if (this.disabled) {
      return true;
    }

    if (this.isUploading) {
      return true;
    }

    return false;
  }

  private resolveUploadFolder(): string {
    const trimmedFolder = this.folder.trim();
    if (!trimmedFolder) {
      return "";
    }

    const normalizedFolder = trimmedFolder.replace(/\/+$/, "");
    if (!normalizedFolder.includes("/")) {
      return normalizedFolder;
    }

    const folderSegments = normalizedFolder.split("/").filter((segment) => segment.trim().length > 0);
    const lastSegment = folderSegments[folderSegments.length - 1]?.toLowerCase() ?? "";
    if (lastSegment !== "attachments") {
      return normalizedFolder;
    }

    const parentSegments = folderSegments.slice(0, -1);
    if (!parentSegments.length) {
      return normalizedFolder;
    }

    return parentSegments.join("/");
  }
}
